import { useCallback } from 'react';
import { useBrandAnalysisData } from '../contexts';

interface UseRouterBasenameReturn {
  /**
   * The basename that React Router was configured with.
   * Empty string if no basename is configured.
   *
   * @example
   * // App mounted at root
   * basename = ""
   *
   * // App mounted at subpath
   * basename = "/ai-brand-insights-tool"
   */
  basename: string;

  /**
   * Strips the router basename from an absolute path to get a router-relative path.
   * This is useful when intercepting navigation events where you have the full URL
   * but need to pass a router-relative path to React Router's navigate function.
   *
   * @param absolutePath - Full path including basename (e.g., "/ai-brand-insights-tool/settings")
   * @returns Router-relative path (e.g., "/settings")
   *
   * @example
   * const { stripBasename } = useRouterBasename();
   * stripBasename("/ai-brand-insights-tool/settings") // returns "/settings"
   * stripBasename("/settings") // returns "/settings" (no basename to strip)
   */
  stripBasename: (absolutePath: string) => string;

  /**
   * Adds the router basename to a router-relative path to get the full absolute path.
   * This is useful when you need to construct full URLs or compare against window.location.
   *
   * @param relativePath - Router-relative path (e.g., "/settings")
   * @returns Full path including basename (e.g., "/ai-brand-insights-tool/settings")
   *
   * @example
   * const { addBasename } = useRouterBasename();
   * addBasename("/settings") // returns "/ai-brand-insights-tool/settings"
   * addBasename("/settings") // returns "/settings" (no basename configured)
   */
  addBasename: (relativePath: string) => string;

  /**
   * Checks if a given path is within the current router's basename scope.
   * Useful for determining if a link should be handled by the current router instance.
   *
   * @param path - Path to check
   * @returns True if the path is within the current basename scope
   *
   * @example
   * const { isWithinBasename } = useRouterBasename();
   * isWithinBasename("/ai-brand-insights-tool/settings") // returns true
   * isWithinBasename("/other-app/page") // returns false
   */
  isWithinBasename: (path: string) => boolean;
}

/**
 * Custom hook that provides utilities for working with React Router basenames.
 *
 * When a React Router application is mounted with a basename (e.g., when embedded
 * in a host application), there's often a need to convert between absolute paths
 * (as seen by the browser) and router-relative paths (as expected by React Router).
 *
 * This hook automatically detects the current basename by comparing the browser's
 * window.location.pathname with React Router's location.pathname, then provides
 * utilities to work with both path formats.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { basename, stripBasename, addBasename } = useRouterBasename();
 *
 *   const handleLinkClick = (href: string) => {
 *     // Convert absolute href to router-relative path
 *     const routerPath = stripBasename(href);
 *     navigate(routerPath);
 *   };
 *
 *   const constructFullUrl = (routerPath: string) => {
 *     // Convert router path to full absolute path
 *     return addBasename(routerPath);
 *   };
 * }
 * ```
 *
 * @returns Object with basename and utility functions
 */
export const useRouterBasename = (): UseRouterBasenameReturn => {
  const { hostAppProps: { basename } } = useBrandAnalysisData();

  // Helper to convert an absolute path to a router-relative path
  const stripBasename = useCallback(
    (absolutePath: string): string => {
      if (!basename || !absolutePath.startsWith(basename)) {
        return absolutePath;
      }
      
      const stripped = absolutePath.slice(basename.length);
      // If absolutePath equals basename, return root path
      return stripped || '/';
    },
    [basename],
  );

  // Helper to convert a router-relative path to an absolute path
  const addBasename = useCallback(
    (relativePath: string): string => {
      if (!basename) {
        return relativePath;
      }

      // Ensure the relative path starts with /
      const normalizedPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
      return `${basename}${normalizedPath}`;
    },
    [basename],
  );

  // Helper to check if a path is within the current basename scope
  const isWithinBasename = useCallback(
    (path: string): boolean => {
      if (!basename) {
        // Accept absolute paths but exclude protocol-relative URLs (//example.com)
        return path.startsWith('/') && !path.startsWith('//');
      }

      return path.startsWith(basename);
    },
    [basename],
  );

  return {
    basename: basename || '',
    stripBasename,
    addBasename,
    isWithinBasename,
  };
};
